enum UserOrganisationType {
  PUBLISHER = 'Publisher',
  RETAILER = 'Retailer',
  ADMINISTRATOR = 'Administrator',
}

interface Auth0UserResponse {
  email: string;
  email_verified: boolean;
  name: string;
  nickname: string;
  picture: string;
  sub: string;
  updated_at: string;
  'https://vaultn.io/access_level': string;
  'https://vaultn.io/account_status': boolean;
  'https://vaultn.io/company_name': string;
  'https://vaultn.io/country_code': number;
  'https://vaultn.io/currency': string;
  'https://vaultn.io/feature_flags': Record<string, string | boolean>;
  'https://vaultn.io/organization_id': string;
  'https://vaultn.io/organization_name': string;
  'https://vaultn.io/owner_role': string;
  'https://vaultn.io/permissions': string[];
  'https://vaultn.io/roles': string[];
  'https://vaultn.io/timezone': string;
  'https://vaultn.io/vaultn_owner_id': number;
}

interface SanitizedAuth0User {
  accessLevel: string;
  accountStatus: boolean;
  companyName: string;
  countryCode: number;
  currency: string;
  email: string;
  emailVerified: boolean;
  featureFlags: Record<string, string | boolean>;
  name: string;
  nickname: string;
  organizationId: string;
  organizationName: string;
  ownerRole: UserOrganisationType;
  permissions: string[];
  picture: string;
  roles: string[];
  sub: string;
  timezone: string;
  updatedAt: string;
  vaultnOwnerId: number;
}

/**
 * Converts Auth0 user response with custom claims to a sanitized user object
 * @param auth0User - Raw user object from Auth0 with custom claims
 * @returns Sanitized user object with clean property names
 */
export function sanitizeAuth0User(auth0User: Auth0UserResponse): SanitizedAuth0User {
  return {
    accessLevel: auth0User['https://vaultn.io/access_level'],
    accountStatus: auth0User['https://vaultn.io/account_status'],
    companyName: auth0User['https://vaultn.io/company_name'],
    countryCode: auth0User['https://vaultn.io/country_code'],
    currency: auth0User['https://vaultn.io/currency'],
    email: auth0User.email,
    emailVerified: auth0User.email_verified,
    featureFlags: auth0User['https://vaultn.io/feature_flags'],
    name: auth0User.name,
    nickname: auth0User.nickname,
    organizationId: auth0User['https://vaultn.io/organization_id'],
    organizationName: auth0User['https://vaultn.io/organization_name'],
    ownerRole: auth0User['https://vaultn.io/owner_role'] as UserOrganisationType,
    permissions: auth0User['https://vaultn.io/permissions'],
    picture: auth0User.picture,
    roles: auth0User['https://vaultn.io/roles'],
    sub: auth0User.sub,
    timezone: auth0User['https://vaultn.io/timezone'],
    updatedAt: auth0User.updated_at,
    vaultnOwnerId: auth0User['https://vaultn.io/vaultn_owner_id'],
  };
}

export type { Auth0UserResponse, SanitizedAuth0User };
export { UserOrganisationType };
